import { useEffect, useMemo, useState } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { ms, relativeTime, shortId } from "../lib/format";

// Detection labels mirror convex/schema.ts detectionLabel.
const LABELS = [
  { key: "correct", text: "Correct", cls: "lbl-correct" },
  { key: "false_pos", text: "False positive", cls: "lbl-fp" },
  { key: "false_neg", text: "False negative", cls: "lbl-fn" },
  { key: "wrong_tier", text: "Wrong tier", cls: "lbl-tier" },
] as const;
type LabelKey = (typeof LABELS)[number]["key"];

// Screen 3 — Tenant-zero detection review + labeling (Design 2.7 #3, THE key
// surface / the refinement loop). The reactive `detectionQueue` query drives
// the live list + optimistic labeling. Images live in R2 (only the keys are in
// Convex — the moat boundary), so we layer short-lived signed GET URLs on top
// via the `detectionQueueWithUrls` ACTION (queries can't make the network call
// to the signing Worker). URLs are merged into the reactive rows by `_id`; if
// the Worker is unreachable or a URL expires, the tile degrades gracefully to a
// placeholder rather than breaking the queue.
export function Detections() {
  const [onlyUnlabeled, setOnlyUnlabeled] = useState(true);
  const limit = 100;

  // Reactive source of truth — drives the list, label state, and counts. Stays
  // live so a one-click label re-renders instantly (and re-orders when filtering
  // unlabeled-only).
  const queue = useQuery(api.detections.detectionQueue, {
    onlyUnlabeled,
    limit,
  });
  const labelDetection = useMutation(api.detections.labelDetection);

  // Lightbox (click an image to view it large + the description alongside, so the
  // operator can actually compare the scene to the VLM's text).
  const [lightbox, setLightbox] = useState<{
    url: string;
    original: boolean;
    desc?: string;
    score?: number;
  } | null>(null);

  // DEV-ONLY reveal: mints a short-lived signed GET URL for the UNBLURRED
  // original on demand (tenant-zero only; the backend refuses otherwise and
  // returns {downloadUrl: null}). Never preloaded — fetched only on click.
  const mintOriginal = useAction(api.detections.mintOriginalImageUrl);

  // Signed image URLs come from an action (one network round-trip to the
  // Worker). Keyed by detection _id and merged into the reactive rows below.
  const queueWithUrls = useAction(api.detections.detectionQueueWithUrls);
  const [imageUrls, setImageUrls] = useState<Record<string, string | null>>({});

  // Refetch signed URLs whenever the visible set of detections changes (new
  // detections arrived, or the filter toggled). We key the effect on the
  // ordered id list so it re-runs only when membership actually changes — not
  // on every label flip. URLs are short-lived; refetching on set-change keeps
  // them fresh enough for review without a polling loop.
  const idSignature = useMemo(
    () => (queue ? queue.map((d) => d._id).join(",") : ""),
    [queue],
  );
  useEffect(() => {
    if (!queue || queue.length === 0) {
      setImageUrls({});
      return;
    }
    let cancelled = false;
    queueWithUrls({ onlyUnlabeled, limit })
      .then((rows) => {
        if (cancelled) return;
        const next: Record<string, string | null> = {};
        for (const r of rows) next[r._id] = r.sampleImageUrl;
        setImageUrls(next);
      })
      .catch(() => {
        // Worker unreachable / not configured — leave placeholders. The queue
        // itself (labels, metadata) is unaffected.
        if (!cancelled) setImageUrls({});
      });
    return () => {
      cancelled = true;
    };
    // idSignature captures membership changes; onlyUnlabeled is part of it via
    // the query args but listed explicitly for the lint rule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idSignature, onlyUnlabeled]);

  // Per-config (release) count summary over the current queue window.
  const perConfig = useMemo(() => {
    if (!queue) return [];
    const map = new Map<
      string,
      { releaseId?: string; total: number; labeled: number }
    >();
    for (const d of queue) {
      const key = d.releaseId ?? "—";
      const cur = map.get(key) ?? {
        releaseId: d.releaseId,
        total: 0,
        labeled: 0,
      };
      cur.total++;
      if (d.label) cur.labeled++;
      map.set(key, cur);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [queue]);

  return (
    <div className="screen">
      <header className="screen-head">
        <h1>Detections — review &amp; label</h1>
        <p className="screen-sub">
          Tenant-zero (DEV lane) only. Label each detection to feed per-config
          FP/FN metrics. Customer devices never send content here.
        </p>
      </header>

      <div className="toolbar">
        <label className="check inline">
          <input
            type="checkbox"
            checked={onlyUnlabeled}
            onChange={(e) => setOnlyUnlabeled(e.target.checked)}
          />
          <span>Unlabeled only</span>
        </label>
        <span className="muted">
          {queue === undefined ? "" : `${queue.length} in queue`}
        </span>
      </div>

      {queue !== undefined && perConfig.length > 0 && (
        <div className="config-summary">
          {perConfig.map((c) => (
            <span className="config-chip" key={c.releaseId ?? "none"}>
              <span className="config-chip-id">
                rel {c.releaseId ? shortId(c.releaseId) : "—"}
              </span>
              <span className="config-chip-count">
                {c.labeled}/{c.total} labeled
              </span>
            </span>
          ))}
        </div>
      )}

      {queue === undefined ? (
        <Skeleton />
      ) : queue.length === 0 ? (
        <EmptyState
          title={onlyUnlabeled ? "Queue clear" : "No detections yet"}
          body="Detections from tenant-zero devices appear here within ~60s of firing."
        />
      ) : (
        <div className="card-grid">
          {queue.map((d) => (
            <article className="det-card" key={d._id}>
              <ImageTile
                url={imageUrls[d._id]}
                sampleKey={d.sampleImageKey}
                originalKey={d.originalImageKey}
                detectionId={d._id as Id<"detections">}
                mintOriginal={mintOriginal}
                onEnlarge={(u, original) =>
                  setLightbox({
                    url: u,
                    original,
                    desc: d.vlmDescription ?? undefined,
                    score: d.gatekeeperScore ?? undefined,
                  })
                }
              />
              <div className="det-body">
                <div className="det-meta">
                  <span className="chip">
                    rel {d.releaseId ? shortId(d.releaseId) : "—"}
                  </span>
                  <span className="chip">dev {shortId(d.deviceId)}</span>
                  <span className="muted">{relativeTime(d.ts)}</span>
                </div>

                <div className="det-models">
                  <span className="model-label">
                    classifier:{" "}
                    <strong>{d.gateModel ?? "open_nsfw"}</strong>
                  </span>
                  <span className="model-label">
                    describer: <strong>{d.describerModel ?? "—"}</strong>
                  </span>
                </div>

                <div className="det-scores">
                  <Stat
                    label="gate"
                    value={
                      d.gatekeeperScore != null
                        ? d.gatekeeperScore.toFixed(3)
                        : "—"
                    }
                  />
                  <Stat label="tier" value={d.tier ?? "—"} />
                  <Stat
                    label="violation"
                    value={
                      d.isViolation == null ? "—" : d.isViolation ? "yes" : "no"
                    }
                  />
                </div>

                {/* Per-leg latency breakdown: where the wall-clock went. */}
                <div className="det-scores det-legs">
                  <Stat label="capture" value={ms(d.captureMs)} />
                  <Stat label="classifier" value={ms(d.gateMs)} />
                  <Stat label="describer" value={ms(d.vlmMs)} />
                  <Stat label="upload" value={ms(d.uploadMs)} />
                  <Stat label="total" value={ms(totalMs(d))} emphasis />
                </div>

                {d.classification && (
                  <div className="det-class">{d.classification}</div>
                )}
                <p className="det-desc">
                  {d.vlmDescription ?? (
                    <span className="muted">no description</span>
                  )}
                </p>

                <div className="label-row">
                  {LABELS.map((l) => (
                    <button
                      key={l.key}
                      className={`btn btn-sm label-btn ${l.cls} ${
                        d.label === l.key ? "selected" : ""
                      }`}
                      onClick={() =>
                        void labelDetection({
                          detectionId: d._id as Id<"detections">,
                          label: l.key as LabelKey,
                        })
                      }
                    >
                      {l.text}
                    </button>
                  ))}
                </div>
                {d.label && (
                  <div className="muted small">
                    labeled: <strong>{d.label}</strong>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {lightbox && (
        <Lightbox
          url={lightbox.url}
          original={lightbox.original}
          desc={lightbox.desc}
          score={lightbox.score}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

// Full-screen image viewer: the image large with the VLM description alongside,
// so the operator can compare what was on screen to what the describer wrote.
// Closes on backdrop click or Esc.
function Lightbox({
  url,
  original,
  desc,
  score,
  onClose,
}: {
  url: string;
  original: boolean;
  desc?: string;
  score?: number;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="lightbox-backdrop" onClick={onClose}>
      <div className="lightbox" onClick={(e) => e.stopPropagation()}>
        <div className="lightbox-imgwrap">
          <img
            src={url}
            alt={original ? "original detection frame (unblurred)" : "blurred sample"}
            className={`lightbox-img ${original ? "is-original" : ""}`}
          />
        </div>
        <aside className="lightbox-side">
          <div className="lightbox-side-head">
            <span
              className={`det-img-tag ${original ? "tag-original" : ""}`}
              style={{ position: "static" }}
            >
              {original ? "ORIGINAL — unblurred (dev)" : "blurred sample"}
            </span>
            <button type="button" className="btn btn-sm" onClick={onClose}>
              Close ✕
            </button>
          </div>
          {score != null && (
            <div className="muted small">gate score {score.toFixed(3)}</div>
          )}
          <h4 className="lightbox-desc-h">describer text</h4>
          <p className="lightbox-desc">
            {desc && desc.trim() ? desc : <span className="muted">no description yet</span>}
          </p>
        </aside>
      </div>
    </div>
  );
}

// Reveal state for the DEV-ONLY original (unblurred) view.
//   "blurred"  — default; showing the blurred sample (or placeholder).
//   "loading"  — mint action in flight after a reveal click.
//   "shown"    — original downloadUrl fetched + rendered, unblurred.
//   "unavail"  — mint returned null (non-tenant-zero / no key / mint failed).
type RevealState = "blurred" | "loading" | "shown" | "unavail";

function ImageTile({
  url,
  sampleKey,
  originalKey,
  detectionId,
  mintOriginal,
  onEnlarge,
}: {
  // undefined = URLs not yet fetched (loading); null = no key / mint failed;
  // string = signed GET URL for the blurred sample.
  url?: string | null;
  sampleKey?: string;
  originalKey?: string;
  detectionId: Id<"detections">;
  // DEV-ONLY: mints a short-lived signed GET URL for the unblurred original.
  mintOriginal: (args: {
    detectionId: Id<"detections">;
  }) => Promise<{ downloadUrl: string | null; expiresAt?: number }>;
  // Open the given image URL large in the lightbox (original=true for unblurred).
  onEnlarge: (url: string, original: boolean) => void;
}) {
  const [errored, setErrored] = useState(false);
  // Reveal state for the unblurred original (dev/review affordance).
  const [reveal, setReveal] = useState<RevealState>("blurred");
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);

  // Reset the broken-image flag if a fresh URL arrives (e.g. after refetch).
  useEffect(() => {
    setErrored(false);
  }, [url]);

  // If the row's identity changes, drop any revealed original (defensive — the
  // parent keys cards by _id so this normally won't fire mid-card).
  useEffect(() => {
    setReveal("blurred");
    setOriginalUrl(null);
  }, [detectionId]);

  const key = sampleKey ?? originalKey;

  // On-demand reveal: fetch the unblurred original ONLY on click.
  function handleReveal() {
    setReveal("loading");
    mintOriginal({ detectionId })
      .then((res) => {
        if (res.downloadUrl) {
          setOriginalUrl(res.downloadUrl);
          setReveal("shown");
        } else {
          setReveal("unavail");
        }
      })
      .catch(() => setReveal("unavail"));
  }

  function reBlur() {
    setReveal("blurred");
    setOriginalUrl(null);
  }

  // --- Revealed original (unblurred, dev-only) ----------------------------
  if (reveal === "shown" && originalUrl) {
    return (
      <div className="det-image has-img revealed">
        <img
          src={originalUrl}
          alt="original detection frame (unblurred, dev)"
          className="det-img-el det-img-original zoomable"
          loading="lazy"
          title="Click to view large"
          onClick={() => onEnlarge(originalUrl, true)}
        />
        <span className="det-img-tag tag-original">
          ORIGINAL — unblurred (dev) · click to enlarge
        </span>
        <button
          type="button"
          className="btn btn-sm reveal-toggle"
          onClick={reBlur}
        >
          Hide / re-blur
        </button>
      </div>
    );
  }

  // --- Blurred sample (default) -------------------------------------------
  if (url && !errored) {
    return (
      <div className="det-image has-img">
        <img
          src={url}
          alt="blurred detection sample"
          className="det-img-el zoomable"
          loading="lazy"
          title="Click to view large"
          onClick={() => onEnlarge(url, false)}
          onError={() => setErrored(true)}
        />
        <span className="det-img-tag">blurred sample · click to enlarge</span>
        <RevealControl
          reveal={reveal}
          onReveal={handleReveal}
          onRetry={handleReveal}
        />
      </div>
    );
  }

  // --- Placeholder: no key, mint failed, URL expired/broke, or loading ----
  const note =
    url === undefined
      ? "loading preview…"
      : errored
        ? "preview expired"
        : key
          ? "no preview URL"
          : "no image key";
  return (
    <div className="det-image placeholder">
      <span className="img-icon">▦</span>
      <span className="img-key mono">{key ?? "no image key"}</span>
      <span className="img-note">{note}</span>
      <RevealControl
        reveal={reveal}
        onReveal={handleReveal}
        onRetry={handleReveal}
      />
    </div>
  );
}

// The DEV reveal affordance: a subtle dev-tagged button that mints the
// unblurred original on click. Clearly marked since it shows real content;
// the whole Detections page is already tenant-zero-only.
function RevealControl({
  reveal,
  onReveal,
  onRetry,
}: {
  reveal: RevealState;
  onReveal: () => void;
  onRetry: () => void;
}) {
  if (reveal === "loading") {
    return (
      <div className="reveal-bar">
        <span className="reveal-status muted">revealing original…</span>
      </div>
    );
  }
  if (reveal === "unavail") {
    return (
      <div className="reveal-bar">
        <span className="reveal-status reveal-unavail">
          original unavailable
        </span>
        <button
          type="button"
          className="btn btn-sm reveal-btn"
          onClick={onRetry}
        >
          retry
        </button>
      </div>
    );
  }
  // Default "blurred" state — the reveal button.
  return (
    <div className="reveal-bar">
      <span className="reveal-dev-tag">DEV</span>
      <button type="button" className="btn btn-sm reveal-btn" onClick={onReveal}>
        Reveal original (unblurred)
      </button>
    </div>
  );
}

// Sum the measured per-leg times (capture/gate/vlm/upload). Falls back to the
// legacy single latencyMs when no leg breakdown is present (older rows).
function totalMs(d: {
  captureMs?: number | null;
  gateMs?: number | null;
  vlmMs?: number | null;
  uploadMs?: number | null;
  latencyMs?: number | null;
}): number | undefined {
  const legs = [d.captureMs, d.gateMs, d.vlmMs, d.uploadMs].filter(
    (n): n is number => typeof n === "number",
  );
  if (legs.length > 0) return legs.reduce((a, b) => a + b, 0);
  return typeof d.latencyMs === "number" ? d.latencyMs : undefined;
}

function Stat({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className={`kpi-mini${emphasis ? " kpi-mini-emph" : ""}`}>
      <span className="kpi-mini-label">{label}</span>
      <span className="kpi-mini-value">{value}</span>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      <div className="empty-body">{body}</div>
    </div>
  );
}

function Skeleton() {
  return <div className="skeleton">Loading…</div>;
}
